<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useBusinessStore } from '@/@vxapp/Businesses/useBusinessStore'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import BusinessDialog from '@/components/Dialogs/Businesses/BusinessDialog.vue'
import BusinessListToolbar from '@/components/ServiceLists/Businesses/BusinessListToolbar.vue'
import BusinessOperationsList from '@/components/ServiceLists/Businesses/BusinessOperationsList.vue'
import {
  BusinessListSort,
  type BusinessListQueryParams,
  type BusinessListRecord,
  type BusinessPaginationMeta,
} from '@/components/ServiceLists/Businesses/businessListTypes'
import { DialogDisplayType } from '@/enums/dialog-display-type'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'

setPageTitle(PageMeta.Businesses.List.Title)
definePage({ meta: PageMeta.Businesses.List.Meta })

interface BusinessOperationsListApi {
  fetchBusinesses: () => Promise<void>
}

const defaultQueryParams: BusinessListQueryParams = {
  sort: BusinessListSort.Newest,
  page: 1,
  limit: 10,
  search: undefined,
}

const allowedLimits = [10, 25, 50, 100]
const managedQueryKeys = Object.keys(defaultQueryParams) as Array<keyof BusinessListQueryParams>
const businessStore = useBusinessStore()
const router = useRouter()
const route = useRoute()
const notify = useAppNotifications()
const businessOperationsList = useTemplateRef<BusinessOperationsListApi>('businessOperationsList')
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)
const isBusinessDialogVisible = shallowRef(false)
const businessData = ref<BusinessListRecord | Record<string, unknown> | null>(null)
const dialogDisplayType = shallowRef<number>(DialogDisplayType.CREATE)
let internallySyncedRouteQueryKey: string | null = null

const queryParams = computed<BusinessListQueryParams>(() =>
  normalizeQueryParams(businessStore.queryParams as Record<string, unknown> | null)
)

function normalizeQueryValue(value: unknown) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
    ? String(normalizedValue)
    : undefined
}

function normalizePositiveInteger(value: unknown, fallback: number) {
  const parsedValue = Number.parseInt(normalizeQueryValue(value) ?? '', 10)

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeLimit(value: unknown) {
  const normalizedLimit = normalizePositiveInteger(value, defaultQueryParams.limit)

  return allowedLimits.includes(normalizedLimit) ? normalizedLimit : defaultQueryParams.limit
}

function normalizeQueryParams(source: Record<string, unknown> | null): BusinessListQueryParams {
  const search = normalizeQueryValue(source?.search)?.trim()

  return {
    sort: normalizeQueryValue(source?.sort) || defaultQueryParams.sort,
    page: normalizePositiveInteger(source?.page, defaultQueryParams.page),
    limit: normalizeLimit(source?.limit),
    search: search || undefined,
  }
}

function queryParamsMatch(left: BusinessListQueryParams, right: BusinessListQueryParams) {
  return managedQueryKeys.every(key => left[key] === right[key])
}

function applyRouteQuery() {
  const currentQuery = normalizeQueryParams(
    businessStore.queryParams as Record<string, unknown> | null
  )

  const routeQuery = normalizeQueryParams(route.query)

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  Object.assign(businessStore.queryParams, currentQuery, routeQuery)

  return true
}

function buildRouteQuery() {
  const nextQuery = { ...route.query }

  for (const key of managedQueryKeys) delete nextQuery[key]

  for (const key of managedQueryKeys) {
    const value = queryParams.value[key]

    if (value !== defaultQueryParams[key] && value !== undefined) nextQuery[key] = String(value)
  }

  return nextQuery
}

async function syncRouteQuery() {
  const nextQuery = buildRouteQuery()
  const nextQueryKey = JSON.stringify(nextQuery)

  if (JSON.stringify(route.query) === nextQueryKey) return

  internallySyncedRouteQueryKey = nextQueryKey
  await router.replace({ query: nextQuery })
}

async function refreshBusinesses() {
  await businessOperationsList.value?.fetchBusinesses()
}

async function updateQueryParams(patch: Partial<BusinessListQueryParams>) {
  Object.assign(businessStore.queryParams, queryParams.value, patch)

  if (isRouteQueryReady.value) await syncRouteQuery()

  await nextTick()
  await refreshBusinesses()
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({ limit: normalizeLimit(limit), page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

async function handleClear() {
  Object.assign(businessStore.queryParams, defaultQueryParams)

  if (isRouteQueryReady.value) await syncRouteQuery()

  await nextTick()
  await refreshBusinesses()
}

function handleLoaded(pagination: BusinessPaginationMeta) {
  resultCount.value = pagination.entriesFound
}

async function handleCreateBusiness() {
  await businessStore.fetchTemplate()
  businessData.value = businessStore.template as Record<string, unknown>
  dialogDisplayType.value = DialogDisplayType.CREATE
  isBusinessDialogVisible.value = true
}

async function handleCreate(newBusinessData: Record<string, unknown>) {
  const { responseCode, message, business } = await businessStore.create(newBusinessData)
  const createdBusiness = business as unknown as BusinessListRecord | null

  if (responseCode !== ResponseCode.CREATED || !createdBusiness?.businessGUID) {
    notify.error(message ?? 'Error creating business')

    return
  }

  notify.success(message)
  await refreshBusinesses()
  await router.push({
    name: PageMeta.Businesses.preview.name as 'setup-businesses-view-guid',
    params: { guid: createdBusiness.businessGUID },
  })
}

async function handleEdit(business: BusinessListRecord) {
  const { responseCode } = await businessStore.fetch(business.businessGUID)

  if (responseCode !== ResponseCode.OK || !businessStore.business) {
    notify.error('Business could not be loaded for editing')

    return
  }

  businessData.value = businessStore.business as unknown as BusinessListRecord
  dialogDisplayType.value = DialogDisplayType.EDIT
  isBusinessDialogVisible.value = true
}

async function handleUpdate(updatedBusinessData: Record<string, unknown>) {
  const { responseCode, message } = await businessStore.update(updatedBusinessData)

  if (responseCode !== ResponseCode.OK) {
    notify.error(message ?? 'Error updating business')

    return
  }

  notify.success(message)
  await refreshBusinesses()
}

onMounted(async () => {
  await businessStore.init()
  applyRouteQuery()
  isRouteQueryReady.value = true
  await nextTick()
  await refreshBusinesses()
})

watch(
  () => route.query,
  async () => {
    const routeQueryKey = JSON.stringify(route.query)
    const wasSyncedInternally = internallySyncedRouteQueryKey === routeQueryKey

    if (wasSyncedInternally) internallySyncedRouteQueryKey = null

    if (!isRouteQueryReady.value || wasSyncedInternally || !applyRouteQuery()) return

    await nextTick()
    await refreshBusinesses()
  },
  { deep: true }
)
</script>

<template>
  <section
    class="businesses-workspace"
    data-testid="businesses-workspace"
  >
    <VCard class="businesses-workspace__hero">
      <VCardItem>
        <div class="businesses-workspace__hero-content">
          <div>
            <span class="businesses-workspace__eyebrow">Organisation setup</span>
            <VCardTitle class="businesses-workspace__title">Businesses</VCardTitle>
            <VCardSubtitle class="businesses-workspace__subtitle">
              Maintain legal identities, parent relationships, registrations, and platform
              connections.
            </VCardSubtitle>
          </div>
          <div class="businesses-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>
      <VDivider />
      <VCardActions class="businesses-workspace__actions">
        <VBtn
          data-testid="new-business-action"
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreateBusiness"
        >
          New business
        </VBtn>
        <VBtn
          data-testid="hero-refresh-businesses"
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="refreshBusinesses"
        >
          Refresh businesses
        </VBtn>
      </VCardActions>
    </VCard>

    <BusinessListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="businessStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
      @refresh="refreshBusinesses"
    />

    <BusinessOperationsList
      ref="businessOperationsList"
      :query-params="queryParams"
      @page-change="handlePageChange"
      @edit="handleEdit"
      @loaded="handleLoaded"
    />

    <BusinessDialog
      v-if="isBusinessDialogVisible && businessData"
      v-model:is-dialog-visible="isBusinessDialogVisible"
      :display-type="dialogDisplayType"
      :business-data="businessData"
      @create="handleCreate"
      @update="handleUpdate"
    />
  </section>
</template>

<style scoped lang="scss">
.businesses-workspace {
  display: grid;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.businesses-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.businesses-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
}

.businesses-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.businesses-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.businesses-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.businesses-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.businesses-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.businesses-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.businesses-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .businesses-workspace {
    gap: 1rem;
  }

  .businesses-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .businesses-workspace__metric {
    min-inline-size: 0;
  }

  .businesses-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .businesses-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
